/*
 * The sticky buy bar.
 *
 * Two jobs. It shows itself whenever the page's own add-to-cart button is off
 * screen, and gets out of the way the moment that button is in view, so the bar
 * never competes with the control it stands in for.
 *
 * And it keeps up with the variant: Dawn re-renders the product section on every
 * variant change and publishes the fresh markup, so the bar lifts its own
 * already-rendered contents out of that and swaps them in. Prices, sold-out
 * wording and the disabled state therefore stay in Liquid, formatted once.
 */
if (!customElements.get('product-buy-bar')) {
  customElements.define(
    'product-buy-bar',
    class ProductBuyBar extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.submitButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.backInStock = document.getElementById(`BackInStock-${this.sectionId}`);

        // Without a control to stand in for there is no sensible moment to
        // appear.
        if (!this.pickTrigger()) return;

        this.observe();
        this.listenForVariantChange();
        this.mirrorSubmitState();
        this.addEventListener('click', this.jumpToBackInStock);
      }

      /*
       * Which control on the page this bar is standing in for, and therefore
       * which one it watches for going off screen.
       *
       * Normally the add button. On a sold-out variant that button is taken off
       * the page altogether and the panel asking for an email address stands
       * where it was, so the panel is what the bar has to watch: watching a
       * button that is no longer laid out means it is never on screen, and the
       * bar would sit there from the top of the page down.
       */
      pickTrigger() {
        const submitVisible = this.submitButton && this.submitButton.offsetParent !== null;
        return submitVisible ? this.submitButton : this.backInStock?.querySelector('.back-in-stock__panel');
      }

      /*
       * A sold-out variant offers to write when it is back rather than to be
       * added, and the panel that takes the address is back up the page. The
       * bar carries the buyer to it and puts the cursor in the field, so the
       * offer it made is the thing they arrive at.
       */
      jumpToBackInStock = (event) => {
        const button = event.target.closest('[data-back-in-stock-target]');
        if (!button) return;

        const field = document.getElementById(button.dataset.backInStockTarget);
        if (!field) return;

        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        field.focus({ preventScroll: true });
      };

      disconnectedCallback() {
        this.observer?.disconnect();
        this.stateObserver?.disconnect();
        if (this.variantSubscriber) this.variantSubscriber();
      }

      /*
       * The bar's button submits the same form as the page's, so anything that
       * holds one has to hold the other: a ring with no size chosen, a sold-out
       * variant, a variant still being fetched. Rather than work any of that out
       * again, the bar copies the state of the button it stands in for.
       *
       * And it says why. By the time the bar is on screen the line explaining
       * what is still unanswered has scrolled away with the fields it refers to,
       * so a buyer would otherwise meet a dead button with no reason given.
       */
      mirrorSubmitState() {
        this.stateObserver?.disconnect();

        const button = this.querySelector('.buy-bar__add');
        if (!button) return;

        // The back-in-stock button stands in for no form control, so there is
        // nothing to copy and nothing that should ever disable it.
        if (button.hasAttribute('data-back-in-stock-target')) return;
        if (!this.submitButton) return;

        const blocked = document.querySelector(`#ProductOptionsBlocked-${this.sectionId}`);

        let note = null;
        if (blocked) {
          note = document.createElement('span');
          note.className = 'buy-bar__note';
          button.before(note);
        }

        const sync = () => {
          button.disabled = this.submitButton.hasAttribute('disabled');
          if (note) note.textContent = blocked.hidden ? '' : blocked.textContent.trim();
        };

        sync();

        this.stateObserver = new MutationObserver(sync);
        this.stateObserver.observe(this.submitButton, { attributes: true, attributeFilter: ['disabled'] });
        if (blocked) {
          this.stateObserver.observe(blocked, {
            attributes: true,
            childList: true,
            characterData: true,
            subtree: true,
          });
        }
      }

      observe() {
        if (!('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(
          ([entry]) => {
            // Whichever way the control left the screen, above or below, the bar
            // stands in for it; the instant any part of it is back in view, the
            // bar goes.
            this.classList.toggle('buy-bar--visible', !entry.isIntersecting);
          },
          { threshold: 0 }
        );

        this.watchTrigger();
      }

      watchTrigger() {
        const trigger = this.pickTrigger();
        if (!this.observer || !trigger || trigger === this.trigger) return;

        if (this.trigger) this.observer.unobserve(this.trigger);
        this.trigger = trigger;
        this.observer.observe(trigger);
      }

      listenForVariantChange() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantSubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.sectionId) return;

          const source = data.html.getElementById(`BuyBar-${this.sectionId}`);
          const destination = this.querySelector(`#BuyBar-${this.sectionId}`);
          if (!source || !destination) return;

          destination.innerHTML = source.innerHTML;

          // The swap threw away the button that was being watched, and the note
          // that was inserted beside it, so both have to be set up again.
          this.mirrorSubmitState();

          // Going from in stock to sold out or back swaps which control the bar
          // stands in for. Left to the next frame because the panel that
          // replaces the add button is rebuilt off this same event, and may not
          // have been rebuilt yet.
          requestAnimationFrame(() => this.watchTrigger());
        });
      }
    }
  );
}
